import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

const GOALS = ["Emergency Fund", "Vacation", "New Car", "House", "Debt Payoff", "College", "Other"];
const FREQ = [
  { v: "weekly", label: "Weekly" },
  { v: "biweekly", label: "Every 2 weeks" },
  { v: "semi-monthly", label: "Twice/month" },
  { v: "monthly", label: "Monthly" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [d, setD] = useState({
    monthly_income: 5000,
    pay_frequency: "biweekly",
    household_size: 1,
    financial_goals: [],
    monthly_savings_target: 500,
    housing_cost: 1200,
    transportation_cost: 200,
    debt_payments: 0,
    preferred_savings_goal: "Emergency Fund",
  });

  const total = 5;

  const submit = async () => {
    try {
      await api.post("/onboarding", d);
      await refresh();
      toast.success("You're all set");
      nav("/app");
    } catch (err) {
      toast.error("Could not save onboarding");
    }
  };

  const toggleGoal = (g) => setD((s) => ({ ...s, financial_goals: s.financial_goals.includes(g) ? s.financial_goals.filter((x) => x !== g) : [...s.financial_goals, g] }));

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-6 py-10">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${i <= step ? "bg-slate-900" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 0 && (
          <div>
            <h2 className="font-manrope text-3xl font-extrabold tracking-tight text-slate-900">What do you earn?</h2>
            <p className="text-slate-500 mt-2">We use this to calibrate recommendations to your situation.</p>
            <div className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monthly income (after tax)</label>
                <input data-testid="ob-income-input" type="number" min="0" value={d.monthly_income} onChange={(e) => setD({ ...d, monthly_income: Number(e.target.value) })} className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-slate-900 outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pay frequency</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {FREQ.map((f) => (
                    <button key={f.v} type="button" onClick={() => setD({ ...d, pay_frequency: f.v })} data-testid={`ob-freq-${f.v}`} className={`rounded-xl px-4 py-3 border text-sm font-medium transition-colors duration-200 ${d.pay_frequency === f.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>{f.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Household size</label>
                <input data-testid="ob-household-input" type="number" min="1" value={d.household_size} onChange={(e) => setD({ ...d, household_size: Number(e.target.value) })} className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-slate-900 outline-none" />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="font-manrope text-3xl font-extrabold tracking-tight text-slate-900">What are you working toward?</h2>
            <p className="text-slate-500 mt-2">Pick anything that fits — you can change these later.</p>
            <div className="mt-8 grid grid-cols-2 gap-2">
              {GOALS.map((g) => {
                const sel = d.financial_goals.includes(g);
                return (
                  <button key={g} type="button" data-testid={`ob-goal-${g.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => toggleGoal(g)} className={`rounded-xl px-4 py-3 border text-sm font-medium text-left transition-colors duration-200 ${sel ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    <div className="flex items-center gap-2">{sel && <Check size={16} />} {g}</div>
                  </button>
                );
              })}
            </div>
            <label className="mt-8 block text-xs font-semibold uppercase tracking-wider text-slate-500">Preferred savings goal</label>
            <select data-testid="ob-preferred-goal" value={d.preferred_savings_goal || ""} onChange={(e) => setD({ ...d, preferred_savings_goal: e.target.value })} className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none">
              {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="font-manrope text-3xl font-extrabold tracking-tight text-slate-900">Fixed monthly costs</h2>
            <p className="text-slate-500 mt-2">Approximate is fine.</p>
            <div className="mt-8 space-y-4">
              {[
                { key: "housing_cost", label: "Housing (rent/mortgage)" },
                { key: "transportation_cost", label: "Transportation" },
                { key: "debt_payments", label: "Debt payments" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.label}</label>
                  <input data-testid={`ob-${f.key}`} type="number" min="0" value={d[f.key]} onChange={(e) => setD({ ...d, [f.key]: Number(e.target.value) })} className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-slate-900 outline-none" />
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="font-manrope text-3xl font-extrabold tracking-tight text-slate-900">Monthly savings target</h2>
            <p className="text-slate-500 mt-2">How much would you like to save each month?</p>
            <div className="mt-8">
              <div className="font-manrope text-6xl font-extrabold tracking-tighter text-emerald-600 tabular-nums">${d.monthly_savings_target.toLocaleString()}</div>
              <input data-testid="ob-savings-target" type="range" min="0" max="3000" step="50" value={d.monthly_savings_target} onChange={(e) => setD({ ...d, monthly_savings_target: Number(e.target.value) })} className="mt-6 w-full accent-slate-900" />
              <div className="flex justify-between text-xs text-slate-400 mt-2">
                <span>$0</span><span>$3,000+</span>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="font-manrope text-3xl font-extrabold tracking-tight text-slate-900">You're set.</h2>
            <p className="text-slate-500 mt-2">MoneyWise will use this to tailor your insights.</p>
            <div className="mt-8 bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
              <Row k="Monthly income" v={`$${d.monthly_income.toLocaleString()}`} />
              <Row k="Pay frequency" v={d.pay_frequency} />
              <Row k="Household" v={d.household_size} />
              <Row k="Housing" v={`$${d.housing_cost.toLocaleString()}`} />
              <Row k="Savings target" v={`$${d.monthly_savings_target.toLocaleString()}`} />
              <Row k="Goals" v={d.financial_goals.join(", ") || "—"} />
            </div>
          </div>
        )}

        <div className="mt-10 flex gap-3">
          {step > 0 && <button data-testid="ob-back-btn" onClick={() => setStep((s) => s - 1)} className="flex-1 bg-slate-100 text-slate-900 rounded-full py-4 font-semibold hover:bg-slate-200 transition-colors duration-200">Back</button>}
          {step < total - 1 && <button data-testid="ob-next-btn" onClick={() => setStep((s) => s + 1)} className="flex-1 bg-slate-900 text-white rounded-full py-4 font-semibold hover:bg-slate-800 active:scale-[0.98] transition-transform duration-150">Continue</button>}
          {step === total - 1 && <button data-testid="ob-finish-btn" onClick={submit} className="flex-1 bg-emerald-600 text-white rounded-full py-4 font-semibold hover:bg-emerald-700 active:scale-[0.98] transition-transform duration-150">Enter MoneyWise</button>}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-900 font-medium">{v}</span>
    </div>
  );
}
